import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = '/logo_transparent.png';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <header className="border-b border-slate-200 py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="relative cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="RentalHub" className="h-10 object-contain" />
            <span className="absolute text-[4px] font-semibold text-slate-400 select-none" style={{ top: '28%', right: '12%' }}>TM</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="terms-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back to Home
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-2" data-testid="terms-heading">Terms of Service</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: April 14, 2026</p>

        <div className="prose prose-slate prose-sm max-w-none space-y-8 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using the RentalHub platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms apply to all users of the Service, including property owners, managers, staff members, and administrators.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">2. Description of Service</h2>
            <p>RentalHub is a short-term rental property management platform that provides tools for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Managing properties, units, and reservations</li>
              <li>Synchronising calendars across booking platforms via iCal</li>
              <li>Managing guests, cleaning schedules, and maintenance tasks</li>
              <li>Tracking financial transactions and generating invoices</li>
              <li>Team collaboration with role-based access control</li>
              <li>AI-assisted guest messaging</li>
            </ul>
            <p>The Service is provided "as is" and we reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">3. Account Registration</h2>
            <p>To use the Service, you must create an account and provide accurate, complete, and current information. You are responsible for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorised use of your account</li>
              <li>Ensuring that your use of the Service complies with all applicable laws</li>
            </ul>
            <p>We reserve the right to suspend or terminate accounts that violate these Terms or that have been inactive for an extended period.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">4. Subscription Plans and Payment</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">4.1 Plans</h3>
            <p>RentalHub offers the following subscription plans:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Standard Plan (R250/month):</strong> Up to 5 properties, basic reporting, iCal sync</li>
              <li><strong>Pro Plan (R500/month):</strong> Unlimited properties, advanced analytics, priority iCal sync, AI messaging suggestions, invoice generation</li>
            </ul>
            <p>Both plans include a 14-day free trial. No credit card is required to start the trial.</p>

            <h3 className="text-base font-semibold text-slate-800 mb-2 mt-4">4.2 Billing</h3>
            <p>Subscription fees are billed monthly in South African Rands (ZAR) through our payment processor, Paystack. By subscribing, you authorise us to charge your selected payment method on a recurring basis until you cancel.</p>

            <h3 className="text-base font-semibold text-slate-800 mb-2 mt-4">4.3 Cancellation and Refunds</h3>
            <p>You may cancel your subscription at any time. Upon cancellation, you will retain access to the Service until the end of your current billing period. We do not provide prorated refunds for partial months. If you cancel during the free trial, you will not be charged.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">5. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Violate any applicable law, regulation, or third-party rights</li>
              <li>Upload or transmit viruses, malware, or other malicious code</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Attempt to gain unauthorised access to other users' accounts</li>
              <li>Use the Service for any illegal, fraudulent, or harmful purpose</li>
              <li>Scrape, crawl, or otherwise extract data from the Service</li>
              <li>Resell or redistribute the Service without our written consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">6. Data and Content</h2>
            <h3 className="text-base font-semibold text-slate-800 mb-2">6.1 Your Data</h3>
            <p>You retain ownership of all data and content you upload to the Service ("Your Data"). By using the Service, you grant us a limited licence to use, store, and process Your Data solely for the purpose of providing the Service to you.</p>

            <h3 className="text-base font-semibold text-slate-800 mb-2 mt-4">6.2 Data Accuracy</h3>
            <p>You are responsible for the accuracy and completeness of the data you enter into the Service. We are not liable for any errors, losses, or damages resulting from inaccurate or incomplete data.</p>

            <h3 className="text-base font-semibold text-slate-800 mb-2 mt-4">6.3 Data Export</h3>
            <p>You may export your data from the Service at any time in standard formats (CSV, PDF). We will make reasonable efforts to ensure data portability.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">7. Third-Party Integrations</h2>
            <p>The Service integrates with third-party platforms including Airbnb, Booking.com, Vrbo, and Paystack. These integrations are provided for your convenience and are subject to the respective third parties' terms of service. We are not responsible for the availability, accuracy, or conduct of any third-party service.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">8. Intellectual Property</h2>
            <p>The Service, including its design, features, code, documentation, and branding, is owned by RentalHub and protected by intellectual property laws. The RentalHub name and logo are trademarks of RentalHub. You may not copy, modify, distribute, or create derivative works based on the Service without our express written permission.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">9. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, RentalHub shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of revenue, profits, data, or business opportunities, arising from your use of or inability to use the Service.</p>
            <p>Our total liability for any claims arising from or related to the Service shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">10. Disclaimer of Warranties</h2>
            <p>The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or free of harmful components.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">11. Indemnification</h2>
            <p>You agree to indemnify and hold harmless RentalHub, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">12. Modifications to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after any changes constitutes your acceptance of the revised Terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">13. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the Republic of South Africa, without regard to its conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved in the courts of South Africa.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">14. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p className="font-medium">RentalHub<br />Email: legal@rentalhub.app</p>
          </section>
        </div>
      </main>

      <footer className="py-8 px-4 border-t border-slate-200 text-center">
        <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} RentalHub. All rights reserved.</p>
      </footer>
    </div>
  );
}
